import { z } from 'zod'
import type { ClientKind } from '@/kinds/kind'
import { compact, conflict, invalid } from '@/kinds/kind'
import type { Resource } from '@/db/types'
import { formatTime } from '@/lib/time'

export const suggestionOp = z.object({
  type: z.enum([
    'action',
    'patch',
    'create',
    'page_write',
    'link',
    'delete',
    'start_agent',
    'create_prototype',
    'merge_pr',
    'release',
  ]),
  id: z.string().optional(),
  kind: z.string(),
  action: z.string().optional(),
  args: z.unknown().optional(),
  spec: z.record(z.string(), z.unknown()).optional(),
  labels: z.record(z.string(), z.string()).optional(),
})
export type SuggestionOp = z.infer<typeof suggestionOp>

export const suggestionSpec = z
  .object({
    type: z.enum([
      'plan',
      'triage',
      'schedule',
      'breakdown',
      'answer',
      'extract',
      'write',
      'capture',
      'api',
      'ask',
    ]),
    /** an ask's buttons: Prime's question, answered with a tap */
    options: z.array(z.string().min(1).max(60)).max(4).optional(),
    title: z.string().min(1).max(200),
    rationale: z.string().max(2000).default(''),
    target_id: z.string().optional(),
    prompt: z.string().max(2000).optional(),
    model: z.string().optional(),
    parent_id: z.string().optional(),
    feedback: z.string().max(2000).optional(),
    session_id: z.string().optional(),
    ops: z.array(suggestionOp).max(50),
  })
  .strict()
export type SuggestionSpec = z.infer<typeof suggestionSpec>

/** The stages of a merge batch after its merges (Go's BatchStages). */
export const BATCH_STAGES = ['building', 'shipped', 'live', 'offered', 'held', 'late'] as const
export type BatchStage = (typeof BATCH_STAGES)[number]

/** Where a merge batch stands once its merges are over (Go's BatchEnd). */
export const batchEnd = z.object({
  stage: z.enum(BATCH_STAGES),
  sha: z.string().optional(),
  tag: z.string().optional(),
  reason: z.string().optional(),
  at: z.string(),
})
export type BatchEnd = z.infer<typeof batchEnd>

/** A batch's end is final: nothing more happens to it. */
export const batchDone = (e: BatchEnd | undefined): boolean =>
  !!e && (e.stage === 'live' || e.stage === 'offered' || e.stage === 'held' || e.stage === 'late')

/** A merge card's place in the merge queue; position is counted from the other cards, never stored. */
export const mergeState = z.object({
  state: z.enum(['queued', 'merging', 'updating', 'failed', 'merged']),
  queued_at: z.string(),
  /** the merge batch it joined: the id of the batch's first card */
  batch: z.string().optional(),
  /** the batch releases main once every card has ended (Merge and ship) */
  ship: z.boolean().optional(),
  /** when it conflicted and its agent was asked to merge main, and the head then */
  updating_at: z.string().optional(),
  head: z.string().optional(),
  /** how far the batch has got after its merges */
  end: batchEnd.optional(),
  started_at: z.string().optional(),
  /** the plain sentence for a failure */
  reason: z.string().optional(),
  failed_at: z.string().optional(),
  /** how long Forgejo's merge call took, in ms (merged) */
  took_ms: z.number().int().optional(),
  /** a plain remark on a merged card, e.g. that main moved under it */
  note: z.string().optional(),
})
export type MergeState = z.infer<typeof mergeState>

const MAX_MERGE_REASON = 300
/** Go's suggestion.MaxAnswer: an ask's recorded answer. */
const MAX_ANSWER = 80

/** Trimmed and cut to n code points (Go's clip). */
const clip = (raw: unknown, n: number): string =>
  [...(typeof raw === 'string' ? raw.trim() : '')].slice(0, n).join('')

export const suggestionStatus = z.object({
  state: z.enum(['pending', 'accepted', 'dismissed', 'expired', 'superseded', 'applied', 'reverted']),
  decided_at: z.string().optional(),
  /** when the user marked a capture receipt as seen (it leaves Today) */
  acknowledged_at: z.string().optional(),
  /** what a server-applied capture changed, for revert: {spec: {key: previous|null}, labels, state, project_id} */
  before: z
    .object({
      spec: z.record(z.string(), z.unknown()).optional(),
      labels: z.record(z.string(), z.string().nullable()).optional(),
      state: z.string().optional(),
      project_id: z.string().optional(),
      /** list items as they were before each list action the capture made */
      lists: z
        .array(
          z.object({
            list_id: z.string(),
            action: z.string(),
            items: z.array(z.record(z.string(), z.unknown())).default([]),
          }),
        )
        .optional(),
    })
    .optional(),
  accepted_ops: z.array(z.number().int()).optional(),
  /** what accepting made from create ops, so cards can link to it */
  created: z.array(z.object({ op: z.number().int(), kind: z.string(), id: z.string() }).strict()).optional(),
  /** where a merge card stands in the merge queue (a merged card is simply accepted) */
  merge: mergeState.optional(),
  /** the option the owner tapped on an ask (accept or dismiss {answer}), read back by whoever asked */
  answer: z.string().optional(),
})
export type SuggestionStatus = z.infer<typeof suggestionStatus>
export type SuggestionResource = Resource<SuggestionSpec, SuggestionStatus>

type Merge = NonNullable<SuggestionStatus['merge']>

/** The merge queue's moves on a pending card (Go's applyMerge). */
const applyMerge = (
  st: SuggestionStatus,
  r: SuggestionResource,
  action: string,
  args: unknown,
  at: string,
): SuggestionStatus => {
  const m = st.merge
  const live = !!m && (m.state === 'queued' || m.state === 'merging' || m.state === 'updating')
  const when = formatTime(new Date(at))
  const a = (args ?? {}) as { reason?: unknown; batch?: unknown; ship?: unknown; head?: unknown }
  if (action === 'merge_queue') {
    if (r.spec.ops.length === 0 || r.spec.ops.some((op) => op.type !== 'merge_pr'))
      throw invalid('only a card that merges a pull request can be queued')
    if (live) return st // a second tap joins the first
    const queued: Merge = { state: 'queued', queued_at: when }
    const batch = clip(a.batch, 64)
    if (batch) queued.batch = batch
    if (a.ship === true) queued.ship = true
    return { ...st, merge: queued }
  }
  if (action === 'merge_start') {
    if (!m || (m.state !== 'queued' && m.state !== 'updating')) {
      if (live) return st
      throw conflict('this merge is not queued')
    }
    const merging: Merge = { ...m, state: 'merging', started_at: when }
    delete merging.reason
    delete merging.updating_at
    delete merging.head
    return { ...st, merge: merging }
  }
  if (!m || !live) throw conflict('this merge is not under way')
  if (action === 'merge_update') {
    if (m.state === 'updating') return st // its agent was asked once already
    const updating: Merge = { ...m, state: 'updating', updating_at: when }
    delete updating.started_at
    const reason = clip(a.reason, MAX_MERGE_REASON)
    if (reason) updating.reason = reason
    else delete updating.reason
    const head = clip(a.head, 64)
    if (head) updating.head = head
    else delete updating.head
    return { ...st, merge: updating }
  }
  const reason = clip(a.reason, MAX_MERGE_REASON)
  const failed: Merge = { ...m, state: 'failed', failed_at: when }
  delete failed.started_at
  delete failed.updating_at
  delete failed.head
  if (reason) failed.reason = reason
  else delete failed.reason
  return { ...st, merge: failed }
}

/** A batch's stage after its merges, on one of its cards (Go's applyMergeBatch). */
const applyMergeBatch = (st: SuggestionStatus, args: unknown, at: string): SuggestionStatus => {
  const m = st.merge
  if (!m?.batch) throw conflict('this card is not in a merge batch')
  const a = (args ?? {}) as { stage?: unknown; sha?: unknown; tag?: unknown; reason?: unknown }
  const stage = BATCH_STAGES.find((s) => s === a.stage)
  if (!stage) throw invalid('stage is one of building, shipped, live, offered, held, late')
  const end: BatchEnd = { stage, at: formatTime(new Date(at)) }
  // a later stage keeps what an earlier one learned (the commit, the tag)
  const sha = clip(a.sha, 64) || m.end?.sha || ''
  const tag = clip(a.tag, 120) || m.end?.tag || ''
  const reason = clip(a.reason, MAX_MERGE_REASON)
  if (sha) end.sha = sha
  if (tag) end.tag = tag
  if (reason) end.reason = reason
  return { ...st, merge: { ...m, end } }
}

/** The merge settling a card (Go's applyMerged): accepted, whatever it was before but done. */
const applyMerged = (
  st: SuggestionStatus,
  r: SuggestionResource,
  args: unknown,
  at: string,
): SuggestionStatus => {
  if (r.spec.ops.length === 0 || r.spec.ops.some((op) => op.type !== 'merge_pr'))
    throw invalid('only a card that merges a pull request can be settled as merged')
  const a = (args ?? {}) as { took_ms?: unknown; note?: unknown }
  const when = formatTime(new Date(at))
  const note = [...(typeof a.note === 'string' ? a.note.trim() : '')].slice(0, MAX_MERGE_REASON).join('')
  const merge: NonNullable<SuggestionStatus['merge']> = {
    state: 'merged',
    queued_at: st.merge?.queued_at || when,
  }
  // a merged card stays in its batch: the batch counts it as in
  if (st.merge?.batch) merge.batch = st.merge.batch
  if (st.merge?.ship) merge.ship = true
  if (st.merge?.end) merge.end = st.merge.end
  if (typeof a.took_ms === 'number' && a.took_ms > 0) merge.took_ms = Math.trunc(a.took_ms)
  if (note) merge.note = note
  return { ...st, state: 'accepted', decided_at: when, merge }
}

/** Mirrors internal/kinds/suggestion Apply. accept takes optional {ops: [indexes kept]}; accept and dismiss {answer}. */
export const apply = (
  r: SuggestionResource,
  action: string,
  args: unknown,
  at: string,
): SuggestionResource => {
  const st: SuggestionStatus = { ...r.status }
  if (action === 'acknowledge') {
    // "seen", not a decision: it must work from any state, or a card the user
    // wants rid of can stick to their screen with nothing that shifts it
    st.acknowledged_at = formatTime(new Date(at))
    return { ...r, status: compact(st) }
  }
  if (action === 'merge_batch') return { ...r, status: compact(applyMergeBatch(st, args, at)) }
  if (
    action === 'merge_queue' ||
    action === 'merge_start' ||
    action === 'merge_update' ||
    action === 'merge_fail'
  ) {
    if (st.state !== 'pending') throw conflict(`suggestion is already ${st.state}`)
    return { ...r, status: compact(applyMerge(st, r, action, args, at)) }
  }
  if (action === 'merged') {
    if (st.state === 'accepted' || st.state === 'applied' || st.state === 'reverted')
      throw conflict(`suggestion is already ${st.state}`)
    return { ...r, status: compact(applyMerged(st, r, args, at)) }
  }
  if (action === 'revert') {
    if (st.state !== 'applied')
      throw conflict(`only an applied capture can be reverted; this one is ${st.state}`)
  } else if (action === 'reapply') {
    if (st.state !== 'reverted')
      throw conflict(`only a put-back capture can be filed again; this one is ${st.state}`)
  } else if (st.state !== 'pending') throw conflict(`suggestion is already ${st.state}`)
  if (action === 'revert') st.state = 'reverted'
  else if (action === 'reapply') {
    // what the replay made this time replaces the old, deleted, created set
    st.state = 'applied'
    const created = (args as { created?: unknown } | null)?.created
    delete st.created
    if (Array.isArray(created) && created.length > 0)
      st.created = created
        .filter(
          (c): c is { op: number; kind: string; id: string } =>
            !!c && typeof c === 'object' && typeof (c as { id?: unknown }).id === 'string',
        )
        .map((c) => ({ op: Number(c.op), kind: String(c.kind), id: c.id }))
  } else if (action === 'accept') {
    st.state = 'accepted'
    const ops = (args as { ops?: unknown } | null)?.ops
    // an empty kept list is omitted, matching Go's omitempty
    if (Array.isArray(ops))
      st.accepted_ops = ops.filter(
        (i): i is number => typeof i === 'number' && Number.isInteger(i) && i >= 0 && i < r.spec.ops.length,
      )
    const created = (args as { created?: unknown } | null)?.created
    if (Array.isArray(created) && created.length > 0)
      st.created = created
        .filter(
          (c): c is { op: number; kind: string; id: string } =>
            !!c && typeof c === 'object' && typeof (c as { id?: unknown }).id === 'string',
        )
        .map((c) => ({ op: Number(c.op), kind: String(c.kind), id: c.id }))
    st.answer = clip((args as { answer?: unknown } | null)?.answer, MAX_ANSWER)
  } else if (action === 'dismiss') {
    st.state = 'dismissed'
    st.answer = clip((args as { answer?: unknown } | null)?.answer, MAX_ANSWER)
  } else if (action === 'supersede') st.state = 'superseded'
  else throw invalid(`unknown action ${action}`)
  st.decided_at = formatTime(new Date(at))
  return { ...r, status: compact(st) }
}

const Nothing = () => null

export const suggestionKind: ClientKind<SuggestionSpec, SuggestionStatus> = {
  name: 'Suggestion',
  version: 1,
  spec: suggestionSpec,
  status: suggestionStatus,
  defaults: (spec) => ({ rationale: '', ops: [], ...spec }) as SuggestionSpec,
  validate: () => [],
  initialStatus: () => ({ state: 'pending' }),
  displayName: (r) => r.spec.title,
  apply,
  actions: ['accept', 'dismiss', 'supersede', 'revert', 'acknowledge', 'reapply'],
  ListRow: Nothing,
  Detail: Nothing,
  Form: Nothing,
  workspaceScoped: true,
}
